import { ccsOptionSets } from '@chiroup/core/constants/CCSOptionSets';
import {
  CCSPrimaryQuestionIds,
  CCSPrimaryUuidToAlias,
  CCSSecondaryQuestionIds,
  CCSSecondaryUuidToAlias,
  abbreviatedCCSQuestionsIds,
} from '@chiroup/core/constants/CCSQuestionIds';
import { SectionTitles } from '@chiroup/core/enums/SectionTitles.enum';
import { flattenQuestions } from '@chiroup/core/functions/buildCustomSurveySoapLines';
import { createName } from '@chiroup/core/functions/createName';
import { orderComplaints } from '@chiroup/core/functions/orderComplaints';
import { escapeHtml } from '@chiroup/core/functions/sanitizeChatHtml';
import { getTextFromTiptapJSON } from '@chiroup/core/functions/tiptapHelpers';
import {
  ComplaintSummaryType,
  type ComplaintSummary,
  type QuestionAnswer,
} from '@chiroup/core/types/ComplaintSummary.type';
import { PatientSurvey } from '@chiroup/core/types/PatientSurvey.type';
import { CarePlanComplaint } from '@chiroup/core/types/PatientVisit.type';
import dayjs from 'dayjs';
import {
  buildAutoAccidentDetailsHtml,
  buildCCSContentDetails,
  buildCCSProblem,
  buildSharedExerciseBlock,
  buildSharedInterimBlock,
  buildWorkInjuryDetailsHtml,
  getFirstAnswered,
  renderInterimSections,
  renderPregnancyHtml,
  renderYellowFlagsHtml,
} from './subjectiveStringBuilderHelpers';

// Narrow type for CCS info passed to renderer
type CcsInfoSection = {
  title: string;
  html: string;
  yellowFlags?: string[];
  items?: { questionAnswerMap?: Record<string, QuestionAnswer> }[];
};
type CcsInfoArea = {
  id: string;
  problem: string;
  sections: CcsInfoSection[];
  qaMap?: Record<string, QuestionAnswer>;
  type?: ComplaintSummaryType;
  problemArea?: number;
  rawInterimSections?: any[];
};

const WYSIWYGContent = ({
  complaints,
  ccsInfo,
  totalComplaints,
  indexToStart = 0,
  skipSubjective = false,
}: {
  complaints: CarePlanComplaint[];
  ccsInfo: CcsInfoArea[];
  totalComplaints: number;
  indexToStart?: number;
  skipSubjective?: boolean;
}) => {
  const orderedComplaints = orderComplaints<CarePlanComplaint>(complaints);

  // Accumulate yellow flags across complaints to render once at the end
  const allYellowFlags: string[] = [];
  // Accumulate pregnancy-related items to render once (before Yellow flags)
  const pregnancyStatuses = new Set<string>();
  const pregnancyDueDates = new Set<string>();
  const pregnancyLmpDates = new Set<string>();

  let interimSharedBlock = '';
  // Track global interim "New complaint" status across complaints so any "Yes" wins
  let globalInterimNewComplaintValue: string | undefined;
  let globalInterimNewComplaintDetails: string | undefined;

  const content = orderedComplaints.reduce((str, complaint, index) => {
    let complaintStr = '';

    if (index >= indexToStart && ccsInfo[index]?.problem) {
      const qaMapForComplaint: Record<string, QuestionAnswer> = ccsInfo[index]?.qaMap || {};

      // Remove pregnancy items from this complaint's Additional information and collect them once
      const sections: CcsInfoSection[] = Array.isArray(ccsInfo[index]?.sections)
        ? ccsInfo[index].sections
        : [];
      const additionalIdx = sections.findIndex(
        (s: CcsInfoSection) => s?.title === SectionTitles.AdditionalInformation
      );

      // Attempt to read pregnancy status/dates via QA map (preferred) or item.questionAnswerMap; fallback to HTML regex
      let statusFromIds: string | undefined;
      let dueFromIds: string | undefined;
      let lmpFromIds: string | undefined;
      // Preferred: pull from aggregated qaMap for the complaint
      const statusQAFromMap = qaMapForComplaint?.[CCSPrimaryQuestionIds.pregnantQuestionId];
      const dueConfirmedQAFromMap =
        qaMapForComplaint?.[CCSPrimaryQuestionIds.pregnantConfirmedDateQuestionId];
      const lmpQAFromMap =
        qaMapForComplaint?.[CCSPrimaryQuestionIds.pregnantSuspectedDateQuestionId];

      if (Array.isArray(statusQAFromMap?.selectedValues) && statusQAFromMap.selectedValues[0]) {
        statusFromIds = String(statusQAFromMap.selectedValues[0]);
      }

      if (
        Array.isArray(dueConfirmedQAFromMap?.selectedValues) &&
        dueConfirmedQAFromMap.selectedValues[0]
      ) {
        dueFromIds = String(dueConfirmedQAFromMap.selectedValues[0]);
      }

      if (Array.isArray(lmpQAFromMap?.selectedValues) && lmpQAFromMap.selectedValues[0]) {
        lmpFromIds = String(lmpQAFromMap.selectedValues[0]);
      }

      const additionalSection: CcsInfoSection | undefined = sections[additionalIdx];
      const items: { questionAnswerMap?: Record<string, QuestionAnswer> }[] =
        (additionalSection?.items ?? []) as {
          questionAnswerMap?: Record<string, QuestionAnswer>;
        }[];

      for (const item of items) {
        const qam = item?.questionAnswerMap || {};

        const statusQA = qam?.[CCSPrimaryQuestionIds.pregnantQuestionId];
        const dueConfirmedQA = qam?.[CCSPrimaryQuestionIds.pregnantConfirmedDateQuestionId];
        const lmpQA = qam?.[CCSPrimaryQuestionIds.pregnantSuspectedDateQuestionId];

        if (
          !statusFromIds &&
          Array.isArray(statusQA?.selectedValues) &&
          statusQA.selectedValues[0]
        ) {
          statusFromIds = String(statusQA.selectedValues[0]);
        }

        if (
          !dueFromIds &&
          Array.isArray(dueConfirmedQA?.selectedValues) &&
          dueConfirmedQA.selectedValues[0]
        ) {
          dueFromIds = String(dueConfirmedQA.selectedValues[0]);
        }

        if (!lmpFromIds && Array.isArray(lmpQA?.selectedValues) && lmpQA.selectedValues[0]) {
          lmpFromIds = String(lmpQA.selectedValues[0]);
        }
      }

      if (statusFromIds) pregnancyStatuses.add(statusFromIds.trim());
      if (dueFromIds) pregnancyDueDates.add(dueFromIds.trim());
      if (lmpFromIds) pregnancyLmpDates.add(lmpFromIds.trim());

      if (additionalIdx !== -1 && sections[additionalIdx]?.html) {
        let html = sections[additionalIdx].html as string;
        // Strip the LI rows for these keys from the Additional information section
        html = html
          .replace(/<li[^>]*>\s*Pregnancy status:.*?<\/li>\s*/gis, '')
          .replace(/<li[^>]*>\s*Due date:.*?<\/li>\s*/gis, '')
          .replace(/<li[^>]*>\s*Date of last menstrual period \(approximate\):.*?<\/li>\s*/gis, '');
        const hasLis = /<li\b/i.test(html);
        sections[additionalIdx].html = hasLis ? html : '';
      }

      complaintStr += buildCCSProblem({
        problem: ccsInfo[index]?.problem,
        problemNumber: index + 1,
        totalProblems: totalComplaints,
      });

      // Build sections content for this complaint, skipping Yellow flags here
      // For sections that can be reliably driven by QA, enrich/guard with qaMap
      const qa = (ccsInfo[index]?.qaMap || {}) as Record<string, QuestionAnswer>;

      // Early path for Reeval: trust pre-processed HTML and filter out shared sections
      const typeForFlagsEarly = ccsInfo[index]?.type as ComplaintSummaryType | undefined;

      if (typeForFlagsEarly === ComplaintSummaryType.Reeval) {
        const filteredSections = (ccsInfo[index]?.sections || []).filter((s: any) => {
          const title = String(s?.title || '');

          return !(
            title === SectionTitles.NewProblems ||
            title === SectionTitles.SymptomChanges ||
            title === SectionTitles.AdditionalConcerns ||
            title === SectionTitles.AdditionalInformation ||
            title === SectionTitles.GeneralQuestions ||
            title === SectionTitles.AdditionalInformationTitleCase
          );
        });

        // Note: keep existing section HTML; do not rebuild Quality here
        complaintStr += buildCCSContentDetails({
          sections: filteredSections,
          skipYellowFlags: true,
          suppressSectionHeaders: false,
          suppressBullets: false,
        });

        if (!skipSubjective && complaint?.subjective) {
          complaintStr += `
            <p><strong>Note:</strong></p>
            <ul><li>${complaint.subjective}</li></ul>
          `;
        }

        return (str += complaintStr);
      }

      const typeForFlags = ccsInfo[index]?.type as ComplaintSummaryType | undefined;

      // For interim summaries, use dedicated renderer and consume shared payload
      if (ccsInfo[index]?.type === ComplaintSummaryType.Interim) {
        // Snapshot current QA for New complaint fields
        const detailsQA = qa['interimSymptomsGeneralNewSymptomsTextBox'];
        // Derive shared payload strictly from QA
        const yesNo = qa['interimSymptomsGeneralNewSymptoms']?.selectedValues?.[0];
        const detailsFromQA =
          detailsQA?.selectedValues?.[0] ??
          (detailsQA?.otherValue ? String(detailsQA.otherValue) : undefined);
        const derivedShared:
          | { newComplaintValue?: string; newComplaintDetails?: string }
          | undefined =
          yesNo || detailsFromQA
            ? { newComplaintValue: yesNo, newComplaintDetails: detailsFromQA }
            : undefined;
        const interimHtml = renderInterimSections(
          (ccsInfo[index]?.rawInterimSections || []) as any[],
          qa
        );

        // Aggregate shared "New complaint" status globally so any "Yes" wins
        if (derivedShared && typeof derivedShared.newComplaintValue !== 'undefined') {
          const val = String(derivedShared.newComplaintValue);

          if (val === 'Yes') {
            globalInterimNewComplaintValue = 'Yes';

            if (derivedShared.newComplaintDetails && !globalInterimNewComplaintDetails) {
              globalInterimNewComplaintDetails = derivedShared.newComplaintDetails;
            }
          } else if (val === 'No') {
            if (globalInterimNewComplaintValue !== 'Yes') {
              globalInterimNewComplaintValue = 'No';
            }
          }
        }

        complaintStr += interimHtml;

        if (!skipSubjective && complaint?.subjective) {
          complaintStr += `
            <p>Note: ${complaint.subjective}</p>
          `;
        }

        return (str += complaintStr);
      }

      const sectionsToRender = (ccsInfo[index]?.sections || []).map((section: CcsInfoSection) => {
        // Timing: require both percent-of-days and percent-of-each-day
        if (section.title === SectionTitles.Timing) {
          const hasDaysPrimary =
            Array.isArray(qa[CCSPrimaryQuestionIds.percentOfDaysQuestionId]?.selectedValues) &&
            qa[CCSPrimaryQuestionIds.percentOfDaysQuestionId]?.selectedValues.length > 0;
          const hasDaysSecondary =
            Array.isArray(qa[CCSSecondaryQuestionIds.percentOfDaysQuestionId]?.selectedValues) &&
            qa[CCSSecondaryQuestionIds.percentOfDaysQuestionId]?.selectedValues.length > 0;
          const hasDayPctPrimary =
            Array.isArray(qa[CCSPrimaryQuestionIds.symptomsArePresentQuestionId]?.selectedValues) &&
            qa[CCSPrimaryQuestionIds.symptomsArePresentQuestionId]?.selectedValues.length > 0;
          const hasDayPctSecondary =
            Array.isArray(
              qa[CCSSecondaryQuestionIds.symptomsArePresentQuestionId]?.selectedValues
            ) &&
            qa[CCSSecondaryQuestionIds.symptomsArePresentQuestionId]?.selectedValues.length > 0;
          const hasDays = hasDaysPrimary || hasDaysSecondary;
          const hasDayPct = hasDayPctPrimary || hasDayPctSecondary;

          return hasDays && hasDayPct ? section : { ...section, html: '' };
        }

        // Severity: include items only if their QA exists
        if (section.title === SectionTitles.Severity) {
          const ids = [
            CCSPrimaryQuestionIds.intensityQuestionId,
            CCSPrimaryQuestionIds.intensity2QuestionId,
            CCSPrimaryQuestionIds.interferedQuestionId,
            CCSPrimaryQuestionIds.generalInterferedQuestionId,
            CCSSecondaryQuestionIds.intensityQuestionId,
            CCSSecondaryQuestionIds.intensity2QuestionId,
            CCSSecondaryQuestionIds.interferedQuestionId,
            CCSSecondaryQuestionIds.generalInterferedQuestionId,
          ];
          const hasAny = ids.some(
            id => Array.isArray(qa[id]?.selectedValues) && qa[id]?.selectedValues.length > 0
          );

          return hasAny ? section : { ...section, html: '' };
        }

        // Provocative / Palliative: include if QA has values; if present, rebuild html from QA for accuracy
        if (section.title === SectionTitles.ProvocativeFactors) {
          const hasProv =
            (Array.isArray(qa[CCSPrimaryQuestionIds.aggravatesQuestionId]?.selectedValues) &&
              qa[CCSPrimaryQuestionIds.aggravatesQuestionId]?.selectedValues.length > 0) ||
            (Array.isArray(qa[CCSSecondaryQuestionIds.aggravatesQuestionId]?.selectedValues) &&
              qa[CCSSecondaryQuestionIds.aggravatesQuestionId]?.selectedValues.length > 0);
          if (!hasProv) return { ...section, html: '' };
          const selectedProv = getFirstAnswered(
            qa,
            CCSPrimaryQuestionIds.aggravatesQuestionId,
            CCSSecondaryQuestionIds.aggravatesQuestionId
          );
          const values = (selectedProv as string[]).map((v: string | undefined) =>
            v === 'other' && qa[CCSPrimaryQuestionIds.aggravatesQuestionId]?.otherValue
              ? qa[CCSPrimaryQuestionIds.aggravatesQuestionId]?.otherValue
              : v
          );
          const lis = values
            .filter(Boolean)
            .map((val: string | undefined) => `<li><strong>${String(val)}</strong></li>`)
            .join('\n');
          const html = values.length ? `<ul>\n${lis}\n</ul>` : '';

          return { ...section, html };
        }

        if (section.title === SectionTitles.PalliativeFactors) {
          const hasPall =
            (Array.isArray(qa[CCSPrimaryQuestionIds.reliefQuestionId]?.selectedValues) &&
              qa[CCSPrimaryQuestionIds.reliefQuestionId]?.selectedValues.length > 0) ||
            (Array.isArray(qa[CCSSecondaryQuestionIds.reliefQuestionId]?.selectedValues) &&
              qa[CCSSecondaryQuestionIds.reliefQuestionId]?.selectedValues.length > 0);
          if (!hasPall) return { ...section, html: '' };
          const selectedPall = getFirstAnswered(
            qa,
            CCSPrimaryQuestionIds.reliefQuestionId,
            CCSSecondaryQuestionIds.reliefQuestionId
          );
          const values = (selectedPall as string[]).map((v: string | undefined) =>
            v === 'other' && qa[CCSPrimaryQuestionIds.reliefQuestionId]?.otherValue
              ? qa[CCSPrimaryQuestionIds.reliefQuestionId]?.otherValue
              : v
          );
          const lis = values
            .filter(Boolean)
            .map((val: string | undefined) => `<li><strong>${String(val)}</strong></li>`)
            .join('\n');
          const html = values.length ? `<ul>\n${lis}\n</ul>` : '';

          return { ...section, html };
        }

        // Quality: ensure mainQuestionId has selections
        if (section.title === SectionTitles.Quality) {
          const hasMain =
            getFirstAnswered(
              qa,
              CCSPrimaryQuestionIds.mainQuestionId,
              CCSSecondaryQuestionIds.mainQuestionId
            ).length > 0;
          // Also, if no bilateral selection, strip bilateral line from html
          if (!hasMain) return { ...section, html: '' };
          const mainSel = getFirstAnswered(
            qa,
            CCSPrimaryQuestionIds.mainQuestionId,
            CCSSecondaryQuestionIds.mainQuestionId
          );
          const lis: string[] = [];

          const pushDescOrLabel = (label: string, qaIdPrimary: string, qaIdSecondary: string) => {
            const vals = getFirstAnswered(qa, qaIdPrimary, qaIdSecondary);

            if (vals.length) {
              lis.push(
                `<li><strong>${label}</strong> <span>described as </span><strong>${vals.join(', ')}</strong></li>`
              );
            } else {
              lis.push(`<li><strong>${label}</strong></li>`);
            }
          };

          if (mainSel.includes('Pain / Discomfort')) {
            pushDescOrLabel(
              'Pain / Discomfort',
              CCSPrimaryQuestionIds.bestDescribesQuestionPainId,
              CCSSecondaryQuestionIds.bestDescribesQuestionPainId
            );
          }

          if (mainSel.includes('Altered sensation')) {
            pushDescOrLabel(
              'Altered sensation',
              CCSPrimaryQuestionIds.bestDescribesQuestionAlteredId,
              CCSSecondaryQuestionIds.bestDescribesQuestionAlteredId
            );
          }

          if (mainSel.includes('Weakness')) {
            pushDescOrLabel(
              'Weakness',
              CCSPrimaryQuestionIds.bestDescribesQuestionWeaknessId,
              CCSSecondaryQuestionIds.bestDescribesQuestionWeaknessId
            );
          }

          if (mainSel.includes('Noise / Grinding')) {
            lis.push('<li><strong>Noise / Grinding</strong></li>');
          }

          if (mainSel.includes('Stiffness / Tightness')) {
            lis.push('<li><strong>Stiffness / Tightness</strong></li>');
          }

          const bilatVals = getFirstAnswered(
            qa,
            CCSPrimaryQuestionIds.bilateralQuestionId,
            CCSSecondaryQuestionIds.bilateralQuestionId
          );

          if (bilatVals.length) {
            lis.push(`<li>Bilateral symptoms: ${bilatVals.join(', ')}</li>`);
          }

          const html = lis.length
            ? `<ul>\n${lis.map(s => s.replace(/: ([^<][^<]*)$/, ': <strong>$1</strong>')).join('\n')}\n</ul>`
            : '';

          return { ...section, html };
        }

        // Additional information basics: strip empty basic lines based on QA
        if (section.title === SectionTitles.AdditionalInformation) {
          let html = section.html || '';
          const hasSeek =
            getFirstAnswered(
              qa,
              CCSPrimaryQuestionIds.seekCareQuestionId,
              CCSSecondaryQuestionIds.seekCareQuestionId
            ).length > 0;
          const hasProviders =
            getFirstAnswered(
              qa,
              CCSPrimaryQuestionIds.providerQuestionId,
              CCSSecondaryQuestionIds.providerQuestionId
            ).length > 0;
          const hasNotice =
            getFirstAnswered(
              qa,
              CCSPrimaryQuestionIds.noticeSymptomsQuestionId,
              CCSSecondaryQuestionIds.noticeSymptomsQuestionId
            ).length > 0;
          if (!hasSeek && html) html = html.replace(/<li[^>]*>\s*Sought care:.*?<\/li>\s*/gis, '');

          if (!hasProviders && html) {
            html = html.replace(/<li[^>]*>\s*Providers seen:.*?<\/li>\s*/gis, '');
          }

          if (!hasNotice && html) {
            html = html.replace(/<li[^>]*>\s*Notice symptoms:.*?<\/li>\s*/gis, '');
          }

          return { ...section, html };
        }

        // Suppress General questions for interim (render once at end)
        if (
          (ccsInfo[index]?.type as ComplaintSummaryType | undefined) ===
            ComplaintSummaryType.Interim &&
          section.title === SectionTitles.GeneralQuestions
        ) {
          return { ...section, html: '' };
        }

        // For Reeval, suppress sections not shown in legacy per-complaint output
        if (
          (ccsInfo[index]?.type as ComplaintSummaryType | undefined) === ComplaintSummaryType.Reeval
        ) {
          const title = String(section.title || '');

          if (
            title === SectionTitles.NewProblems ||
            title === SectionTitles.SymptomChanges ||
            title === SectionTitles.AdditionalConcerns ||
            title === SectionTitles.AdditionalInformation ||
            title === SectionTitles.AdditionalInformationTitleCase
          ) {
            return { ...section, html: '' };
          }
        }

        // Accident/work nested sections: gate by cause
        if (
          section.title === SectionTitles.AutoAccidentDetails ||
          section.title === SectionTitles.AutoAccidentDetailsTitleCase
        ) {
          const causeVals = getFirstAnswered(
            qa,
            CCSPrimaryQuestionIds.causedQuestionId,
            CCSSecondaryQuestionIds.causedQuestionId
          );
          const show = causeVals.includes('Auto accident');
          if (!show) return { ...section, html: '' };

          const html = buildAutoAccidentDetailsHtml(qa);

          return { ...section, html };
        }

        if (
          section.title === SectionTitles.WorkInjuryDetails ||
          section.title === SectionTitles.WorkInjuryDetailsTitleCase
        ) {
          const causeVals = getFirstAnswered(
            qa,
            CCSPrimaryQuestionIds.causedQuestionId,
            CCSSecondaryQuestionIds.causedQuestionId
          );
          const show = causeVals.includes('Employment-related injury');

          if (!show) return { ...section, html: '' };

          const html = buildWorkInjuryDetailsHtml(qa);

          return { ...section, html };
        }

        return section;
      });

      const suppressHeaders = typeForFlags === ComplaintSummaryType.Interim;
      const suppressBullets = typeForFlags === ComplaintSummaryType.Interim;

      complaintStr += buildCCSContentDetails({
        sections: sectionsToRender,
        skipYellowFlags: true,
        suppressSectionHeaders: !!suppressHeaders,
        suppressBullets,
      });

      // Collect Yellow flags via QA map by ID (preferred over parsing sections)
      const yfQA = (
        ccsInfo[index]?.qaMap?.[CCSPrimaryQuestionIds.yellowFlagsQuestionId]?.selectedValues || []
      ).filter(Boolean);
      if (yfQA.length) allYellowFlags.push(...yfQA);
    }

    const shouldShowSubjectiveNote =
      !skipSubjective && index >= indexToStart && complaint?.subjective;
    const hasHeader = index >= indexToStart && !!ccsInfo[index]?.problem;

    if (shouldShowSubjectiveNote) {
      // If there is no CCS header for this complaint, add a minimal header so the note is clearly attributed
      if (!hasHeader) {
        const fallbackProblem = complaint?.bodypicker
          ? createName(complaint.bodypicker.parts)
          : complaint?.complaint;
        complaintStr += buildCCSProblem({
          problem: fallbackProblem,
          problemNumber: index + 1,
          totalProblems: totalComplaints,
        });
      }

      complaintStr += `
        <p><strong>Note:</strong></p>
        <ul><li>${complaint?.subjective}</li></ul>
        `;
    }

    str += complaintStr;

    return str;
  }, '');

  // Append Yellow flags once, after all complaints; pregnancy before yellow flags
  const pregnancySection = renderPregnancyHtml({
    statuses: Array.from(pregnancyStatuses),
    dueDates: Array.from(pregnancyDueDates),
    lmpDates: Array.from(pregnancyLmpDates),
  });
  const yellowFlagsSection = renderYellowFlagsHtml(allYellowFlags);

  const withPregnancy = pregnancySection ? content + pregnancySection : content;
  const withYellow = yellowFlagsSection ? withPregnancy + yellowFlagsSection : withPregnancy;

  // Build and append shared interim "New complaint" block once, based on global aggregation
  const finalShared: { newComplaintValue?: string; newComplaintDetails?: string } | undefined =
    typeof globalInterimNewComplaintValue !== 'undefined'
      ? {
          newComplaintValue: globalInterimNewComplaintValue,
          newComplaintDetails: globalInterimNewComplaintDetails,
        }
      : undefined;

  if (finalShared) {
    interimSharedBlock = buildSharedInterimBlock(finalShared);
  }

  return withYellow + (interimSharedBlock || '');
};

// //ORIGINAL V1
// export const getWYSIWYGSubjectiveContentFn = ({
//   complaints,
//   totalComplaints,
//   interimSurvey,
//   reevaluationSurvey,
//   activeComplaintsProblemAreas,
//   surveys,
//   appointmentId,
//   complaintNames,
// }: {
//   complaints: CarePlanComplaint[];
//   totalComplaints: number;
//   interimSurvey?: PatientSurvey;
//   reevaluationSurvey?: PatientSurvey;
//   activeComplaintsProblemAreas: { [key in string]: boolean };
//   surveys?: { [key: string]: PatientSurvey };
//   appointmentId: string;
//   complaintNames?: { [key in string]: { name: string; order: number } };
// }) => {
//   return () => {
//     const activeComplaints = complaints.filter(complaint => {
//       return activeComplaintsProblemAreas[complaint?.id || ''];
//     });
//     const orderedComplaints = orderComplaints(activeComplaints);
//     totalComplaints = orderedComplaints.length;
//     let ccsInfo: CcsInfoArea[] = [];
//     let surveyString = '';

//     if (interimSurvey || reevaluationSurvey) {
//       const completedAt = interimSurvey?.completedAt || reevaluationSurvey?.completedAt;

//       if (completedAt && interimSurvey) {
//         let start = 1;
//         const totalProblemAreas: { [key: string]: boolean } = {};

//         interimSurvey?.survey?.questions?.forEach((question: any) => {
//           if (question?.complaintId) {
//             totalProblemAreas[question.complaintId] = true;
//           }
//         });

//         const totalInterimProblems = Object.keys(totalProblemAreas)?.length;

//         while (start < totalInterimProblems + 1) {
//           const currentComplaintId = orderedComplaints?.[start - 1]?.id;
//           let synopsisStr = buildInterimSynopsis(interimSurvey, totalComplaints, {
//             problemNum: start,
//             generalQuestions: false,
//             complaintId: currentComplaintId,
//           });

//           if (orderedComplaints?.[start - 1]?.subjective) {
//             synopsisStr += `
//               <div>
//                 <strong>Note: </strong>
//                 <ul><li>${orderedComplaints?.[start - 1]?.subjective}</li></ul><br />
//               </div>
//             `;
//           }

//           surveyString += synopsisStr;
//           // Only increment if this complaint hasn't been disabled
//           start++;
//         }

//         surveyString += buildInterimSynopsis(
//           interimSurvey,
//           Object.keys(totalProblemAreas)?.length,
//           { generalQuestions: true }
//         );

//         if (orderedComplaints?.length) {
//           const complaintInfo = orderedComplaints.map(complaint => {
//             if (complaint?.survey) {
//               const complaintAlreadyInInterim = interimSurvey.survey.questions.some(
//                 q => q?.complaintId === complaint?.id
//               );

//               if (!complaintAlreadyInInterim) {
//                 const survey = {
//                   survey: {
//                     questions:
//                       complaint?.surveyQuestions?.survey?.questions ||
//                       complaint?.survey?.survey?.questions ||
//                       [],
//                   },
//                   completedAt: Date.now(),
//                 };
//                 const ccsResponse: any = buildCCSResponse(
//                   survey as {
//                     survey: { questions: SurveyQuestion[] };
//                     completedAt: number;
//                   },
//                   [],
//                   true
//                 );
//                 const ccsResponseAreas = ccsResponse?.areas;

//                 if (ccsResponseAreas?.length) {
//                   ccsResponseAreas[0].id = complaint?.id;
//                 }

//                 return ccsResponseAreas;
//               }
//             }

//             return [];
//           });

//           ccsInfo = complaintInfo.map(areaList => {
//             const firstArea = (
//               Array.isArray(areaList) && areaList.length ? areaList[0] : undefined
//             ) as CCSResponse['areas'][number] | undefined;

//             return firstArea
//               ? (firstArea as unknown as CcsInfoArea)
//               : ({ id: '', problem: '', sections: [] } as CcsInfoArea);
//           });

//           surveyString += WYSIWYGContent({
//             complaints: orderedComplaints,
//             ccsInfo,
//             totalComplaints,
//             indexToStart: 0,
//             skipSubjective: true,
//           });

//           // Include subjective notes for complaints that were added during the encounter
//           // (i.e., not present in the interim survey questions)
//           orderedComplaints.forEach((complaint, _index) => {
//             const isInInterim = interimSurvey.survey.questions.some(
//               (q: SurveyQuestion) => q?.complaintId === complaint?.id
//             );

//             if (!isInInterim && complaint?.subjective) {
//               surveyString += `
//                 <div>
//                   <strong>Note: </strong>
//                   <ul><li>${complaint.subjective}</li></ul><br />
//                 </div>
//               `;
//             }
//           });
//         }

//         return surveyString;
//       } else if (completedAt && reevaluationSurvey && orderedComplaints?.length) {
//         const filteredReeval = reevaluationSurvey?.survey?.questions?.filter(
//           (question: SurveyQuestion) => activeComplaintsProblemAreas[question?.complaintId || '']
//         );

//         const ccsStartIndex = reevaluationSurvey?.survey?.questions?.findIndex(
//           question => question.id === CCSBodyPickerQuestionIds.primary
//         );

//         const reevalStuff = reevaluationSurvey?.survey?.questions?.slice(0, ccsStartIndex);

//         // Build subjective notes keyed by re-eval complaint order
//         const uniqueComplaintIdsInReeval: string[] = [];
//         const seen: Record<string, boolean> = {};
//         filteredReeval?.forEach((q: SurveyQuestion) => {
//           const cid = q?.complaintId || '';

//           if (cid && !seen[cid]) {
//             seen[cid] = true;
//             uniqueComplaintIdsInReeval.push(cid);
//           }
//         });

//         if (complaintNames && Object.keys(complaintNames || {}).length) {
//           uniqueComplaintIdsInReeval.sort(
//             (a, b) => (complaintNames?.[a]?.order || 0) - (complaintNames?.[b]?.order || 0)
//           );
//         }

//         const idToSubjective: Record<string, string> = {};
//         orderedComplaints.forEach(c => {
//           if (c?.id && c?.subjective) idToSubjective[c.id] = c.subjective;
//         });
//         const subjectiveNotesByIndex: { [key in number]: string } = {};
//         uniqueComplaintIdsInReeval.forEach((cid, _idx) => {
//           const note = idToSubjective[cid];
//           if (note) subjectiveNotesByIndex[_idx + 1] = note;
//         });

//         const numComplaintsInReeval = filteredReeval?.reduce(
//           (obj: { [key in string]: boolean }, question: SurveyQuestion) => {
//             obj[question?.complaintId || ''] = true;

//             return obj;
//           },
//           {}
//         );

//         const complaintNumbers = orderedComplaints?.reduce<Record<string, number>>(
//           (obj, complaint, index) => {
//             obj[complaint?.id || ''] = index + 1;

//             return obj;
//           },
//           {}
//         );

//         const reevalSynopsisStrRaw = buildReevaluationSynopsis(reevalStuff, {
//           showName: true,
//           subjectiveNotes: subjectiveNotesByIndex,
//           totalComplaints: orderedComplaints?.length,
//           complaintNames,
//         });
//         const reevalSynopsisStr = reevalSynopsisStrRaw.replace(
//           new RegExp('<p><strong>Notes:</strong>\\s*([^<]+)</p>', 'g'),
//           '<div><strong>Note: </strong><ul><li>$1</li></ul><br /></div>'
//         );
//         const reevalSynopsisHasNotes = reevalSynopsisStrRaw.includes('<strong>Notes:</strong>');
//         surveyString += reevalSynopsisStr;

//         if (!reevalSynopsisHasNotes && Object.keys(subjectiveNotesByIndex || {}).length) {
//           uniqueComplaintIdsInReeval.forEach((cid, _idx) => {
//             const note = subjectiveNotesByIndex[_idx + 1];

//             if (note) {
//               surveyString += `
//                 <div>
//                   <strong>Note: </strong>
//                   <ul><li>${note}</li></ul><br />
//                 </div>
//               `;
//             }
//           });
//         }

//         //below if for complaint that are added in the Reeval survey...
//         const reevalWithJustCCS = JSON.parse(JSON.stringify(reevaluationSurvey));
//         const firstCCSIndex = reevalWithJustCCS.survey.questions.findIndex(
//           (question: SurveyQuestion) => question.id === CCSBodyPickerQuestionIds.primary
//         );
//         reevalWithJustCCS.survey.questions =
//           reevalWithJustCCS.survey.questions.slice(firstCCSIndex);
//         const ccsInfoInReeval = buildCCSResponse(reevalWithJustCCS, [], true)?.areas;

//         if (ccsInfoInReeval?.[0]?.problem) {
//           const ccsStr = ccsInfoInReeval?.reduce((str: string, area: any) => {
//             if (area?.problem) {
//               str +=
//                 `<h3><strong>` +
//                 area?.problem?.slice(0, 1).toUpperCase() +
//                 area?.problem?.slice(1) +
//                 `</strong></h3>`;
//             }

//             str += `<p>Complaint ${
//               complaintNumbers[area?.complaintId || '']
//             } of ${orderedComplaints?.length}</p>`;

//             str += '<div>';
//             area?.sections?.forEach((section: any) => {
//               if (section?.html) {
//                 str += section?.html;
//               }
//             });
//             str += '</div>';

//             return str;
//           }, '');

//           surveyString += ccsStr;
//         }

//         const complaintInfo = orderComplaints(orderedComplaints).map(complaint => {
//           if (complaint?.survey) {
//             const complaintAlreadyInReeval = reevaluationSurvey.survey.questions.some(
//               q => q.complaintId === complaint?.id
//             );

//             if (!complaintAlreadyInReeval) {
//               const survey: any = {
//                 survey: complaint?.survey,
//               };

//               return buildCCSResponse(
//                 survey as {
//                   survey: { questions: SurveyQuestion[] };
//                   completedAt: number;
//                 },
//                 [],
//                 true
//               )?.areas;
//             }
//           }

//           return [];
//         });
//         ccsInfo = complaintInfo
//           .map(areaList => {
//             const firstArea = (
//               Array.isArray(areaList) && areaList.length ? areaList[0] : undefined
//             ) as CCSResponse['areas'][number] | undefined;

//             return firstArea ? (firstArea as unknown as CcsInfoArea) : undefined;
//           })
//           .filter((a): a is CcsInfoArea => !!a);

//         //this below will populate any other orderedComplaints that are not in the reeval like an additional complaint survey done in the encounter.
//         surveyString += WYSIWYGContent({
//           complaints: orderedComplaints,
//           ccsInfo,
//           totalComplaints,
//           indexToStart: Object.keys(numComplaintsInReeval)?.length,
//         });

//         return surveyString;
//       }

//       if (completedAt) return surveyString;
//     }

//     if (orderedComplaints?.length && surveys && Object.keys(surveys).length) {
//       const complaintsWithSurveys = orderedComplaints.reduce(
//         (obj: { [key: string]: boolean }, complaint) => {
//           if (complaint?.surveyId) {
//             obj[complaint.surveyId] = false;
//           }

//           return obj;
//         },
//         {}
//       );
//       const ccsResponses = orderedComplaints.map((complaint, index) => {
//         const survey = surveys[complaint.surveyId as string];
//         const surveyFromThisAppt =
//           survey?.appointmentId === appointmentId || survey?.encounterId === appointmentId;

//         if (
//           survey?.survey?.questions &&
//           surveyFromThisAppt &&
//           complaint?.surveyId &&
//           !complaintsWithSurveys[complaint?.surveyId]
//         ) {
//           complaintsWithSurveys[complaint?.surveyId] = true;

//           const complaintBodypickerIds = Object.keys(complaintNames || {});
//           const builtCCSResponse = buildCCSResponse(
//             survey,
//             [],
//             true,
//             complaintBodypickerIds,
//             orderedComplaints
//           )?.areas;

//           return builtCCSResponse;
//         } else if (!survey?.appointmentId) {
//           //THIS IS FOR THE CASE IF THEY HAVE A CARRIED OVER COMPLAINT WITH NO SURVEY FOR THAT COMPLAINT
//           // COMPLETED IN THIS ENCOUNTER
//           // SPECIFICALLY FOR THE ENCOUNTER CREATE BUTTON WHICH DOES NOT HAVE AN APPOINTMENT ID

//           const problem = complaint?.bodypicker
//             ? createName(complaint?.bodypicker.parts)
//             : complaint.complaint;

//           return [
//             {
//               problem,
//               sections: [],
//               problemArea: complaint?.problemArea,
//               string: buildCCSProblem({
//                 problem,
//                 problemNumber: index + 1,
//                 totalProblems: totalComplaints,
//               }),
//             },
//           ];
//         }

//         return undefined;
//       });

//       const areasFlat = ccsResponses.filter(res => !!res).flat() as CCSResponse['areas'];
//       const areasByComplaintId: Record<string, CCSResponse['areas'][number]> = {};
//       areasFlat.forEach((area: CCSResponse['areas'][number]) => {
//         if (area?.complaintId) {
//           areasByComplaintId[area.complaintId] = area;
//         }
//       });
//       ccsInfo = orderedComplaints.map((c, _i) => {
//         const area = areasByComplaintId[c?.id as string];

//         return area
//           ? ({
//               id: c?.id || '',
//               problem: area.problem,
//               sections: area.sections as unknown as CcsInfoSection[],
//               problemArea: area.problemArea,
//             } as CcsInfoArea)
//           : ({ id: c?.id || '', problem: '', sections: [] } as CcsInfoArea);
//       });
//     }

//     const v1Result = WYSIWYGContent({
//       complaints: orderedComplaints,
//       ccsInfo,
//       totalComplaints,
//     });

//     return v1Result;
//   };
// };

// NEW VERSION: Works entirely with complaintSummaries data from visitForm
export const getWYSIWYGSubjectiveContentFnV2 = ({
  complaints,
  totalComplaints: _totalComplaints,
  complaintSummaries,
  activeComplaintsProblemAreas,
  complaintNames,
  surveys,
}: {
  complaints: CarePlanComplaint[];
  totalComplaints: number;
  complaintSummaries?: { [complaintId: string]: ComplaintSummary };
  activeComplaintsProblemAreas: { [key in string]: boolean };
  complaintNames?: { [key in string]: { name: string; order: number } };
  surveys?: { [patientSurveyId: string]: PatientSurvey };
}) => {
  return () => {
    // Filter to only active complaints
    const activeComplaints = complaints.filter(complaint => {
      return activeComplaintsProblemAreas[complaint?.id || ''];
    });

    const orderedComplaints = orderComplaints(activeComplaints);
    const finalTotalComplaints = orderedComplaints.length;

    // Unified builder path: synthesize subjective from complaintRelatedQuestions
    const hasUnified = orderedComplaints.some(
      c => !!complaintSummaries?.[c.id || '']?.complaintRelatedQuestions
    );

    if (hasUnified) {
      const allYellowFlags: string[] = [];
      const pregnancyStatuses = new Set<string>();
      const pregnancyDueDates = new Set<string>();
      const pregnancyLmpDates = new Set<string>();
      let globalInterimNewComplaintValue: string | undefined;
      let globalInterimNewComplaintDetails: string | undefined;
      let globalInterimExerciseFrequency: string | undefined;
      let globalInterimExerciseImpediments: string[] = [];
      let globalInterimADLImplementation: string | undefined;
      let globalInterimCustomExtras: string | undefined;

      const unifiedHtml = orderedComplaints.reduce((acc, complaint, idx) => {
        const complaintId = complaint?.id || '';
        const summary = complaintSummaries?.[complaintId];
        const name = complaintNames?.[complaintId]?.name || complaint.complaint || '';
        const header = buildCCSProblem({
          problem: name,
          problemNumber: idx + 1,
          totalProblems: orderedComplaints.length,
        });

        if (!summary?.complaintRelatedQuestions) {
          // No summary data, but still add subjective note if present
          let body = '';

          if (complaint?.subjective) {
            const subjectiveText = getTextFromTiptapJSON(complaint.subjective);
            body += `\n<p><strong>Note:</strong></p>\n<ul><li>${subjectiveText}</li></ul>`;
          }

          return acc + header + body;
        }

        const qa = summary.complaintRelatedQuestions as unknown as Record<string, QuestionAnswer>;
        const useSecondary = !!qa[CCSSecondaryQuestionIds.mainQuestionId];
        // Summaries created before behavesAs/subjectiveTemplate were stamped
        // (or by paths that skip stamping) fall back to the survey definition,
        // so the first render and post-unsign regeneration take the same branch.
        const patientSurvey = summary?.patientSurveyId
          ? surveys?.[summary.patientSurveyId]
          : undefined;
        const summaryBehavesAs = summary?.behavesAs || patientSurvey?.survey?.behavesAs;
        const summarySubjectiveTemplate =
          summary?.subjectiveTemplate || patientSurvey?.survey?.subjectiveTemplate;

        let body = '';

        if (summaryBehavesAs === 'ccs' || summaryBehavesAs === 'abbreviatedCCS') {
          // Custom CCS: use subjectiveTemplate if configured, otherwise Q&A fallback
          let summaryAliasMap = summary?.templateAliasMap;

          // Fallback: if no alias map was stored on the summary, build one from
          // CCS constants based on which question IDs appear in the QA map.
          if (!summaryAliasMap && summarySubjectiveTemplate) {
            const uuidToAlias = useSecondary ? CCSSecondaryUuidToAlias : CCSPrimaryUuidToAlias;
            const fallback: Record<string, string> = {};
            let count = 0;
            for (const [uuid, alias] of Object.entries(uuidToAlias)) {
              if (qa[uuid]) {
                fallback[alias] = uuid;
                count++;
              }
            }
            if (count > 0) {
              summaryAliasMap = fallback;
            }
          }

          body += buildCustomCCSSubjective(
            qa,
            summarySubjectiveTemplate,
            summaryAliasMap,
            patientSurvey
          );
        } else if (
          (summaryBehavesAs === 'reevaluation' || summaryBehavesAs === 'interim') &&
          summarySubjectiveTemplate
        ) {
          // Custom reeval/interim: use subjectiveTemplate with alias map
          body += buildCustomCCSSubjective(
            qa,
            summarySubjectiveTemplate,
            summary?.templateAliasMap,
            patientSurvey
          );
        } else if (summary.type === ComplaintSummaryType.Initial) {
          body += buildInitialSubjectiveFromUnified(qa, useSecondary, {
            pregnancyStatuses,
            pregnancyDueDates,
            pregnancyLmpDates,
            allYellowFlags,
          });
          // Append any custom questions not covered by standard CCS IDs
          body += buildExtraQuestionsSubjective(qa, useSecondary);
        } else if (summary.type === ComplaintSummaryType.Interim) {
          const get = (id?: string) => getUnifiedValues(qa, id);
          const statusKey = Object.keys(qa).find(k => k.includes('interimSymptomsGeneralStatus'));
          const vasKey = Object.keys(qa).find(k => k.includes('interimSymptomsGeneralVAS'));
          // Custom surveys can change a built-in question's type or options, so
          // built-in formatting applies only to the answer shape it expects;
          // anything else prints the patient's selected labels.
          const status = statusKey ? get(statusKey).join(', ') || undefined : undefined;
          const vasValues = vasKey ? get(vasKey) : [];
          const vasType = vasKey ? qa[vasKey]?.questionType : undefined;
          const vasRaw = vasValues[0];
          let vas = vasRaw;

          if (vasValues.length > 1 || (vasType && vasType !== 'slider')) {
            vas = escapeHtml(vasValues.join(', '));
          } else if (vasRaw && /[^0-9.]/.test(String(vasRaw))) {
            // sanitize/clean for display purposes
            vas = String(vasRaw).replace(/[^0-9.]/g, '');
          }

          // For interim, make status and VAS flat items (no section header)
          if (status) {
            body += `<p><strong>Status: ${status}</strong></p>`;
          }

          if (vas) {
            body += `<p><strong>VAS: ${vas}</strong></p>`;
          }

          // Imported interim questions use the shorter percent ids; only an
          // answered one counts so an unanswered question can't print "0%".
          const answeredKey = (fragment: string) =>
            Object.keys(qa).find(k => k.includes(fragment) && get(k).length > 0);
          const pdKey =
            Object.keys(qa).find(k => k.includes('interimSymptomsSpecificPainPercent')) ||
            answeredKey('interimPainPercent');
          const peKey =
            Object.keys(qa).find(k => k.includes('interimSymptomsSpecificPresentPercent')) ||
            answeredKey('interimPresentPercent');
          const pd = pdKey ? Number(get(pdKey)[0] || '') : NaN;
          const pe = peKey ? Number(get(peKey)[0] || '') : NaN;
          const timParts: string[] = [];

          if (pdKey && !isFinite(pd) && get(pdKey).length) {
            body += `<p><strong>Pain frequency: ${escapeHtml(get(pdKey).join(', '))}</strong></p>`;
          }

          if (peKey && !isFinite(pe) && get(peKey).length) {
            body += `<p><strong>Pain presence: ${escapeHtml(get(peKey).join(', '))}</strong></p>`;
          }

          if (isFinite(pd)) {
            timParts.push(
              `Symptoms present <strong>${timingDescriptor(pd, 'days')}</strong> (${pd}% of days)`
            );
          }

          if (isFinite(pe)) {
            timParts.push(
              `<strong>${timingDescriptor(pe, 'eachDay')}</strong> each day (${pe}% of the day)`
            );
          }

          if (timParts.length) {
            body += `<p><span style="font-weight: bold;">${SectionTitles.Timing}</span></p><ul><li>${timParts.join(', ')}</li></ul>`;
          }

          const provKey = Object.keys(qa).find(k =>
            k.includes('interimSymptomsSpecificProvocative')
          );
          const pallKey = Object.keys(qa).find(k =>
            k.includes('interimSymptomsSpecificPalliative')
          );
          const flexKey = Object.keys(qa).find(k =>
            k.includes('interimSymptomsSpecificFlexibility')
          );
          // For interim, add provocative/palliative as flat items (no "Symptom specifics" grouping)
          const provValues = provKey ? get(provKey) : [];

          if (provValues.length) {
            body += `<p><strong>ADL improvements: ${provValues.join(', ')}</strong></p>`;
          }

          const pallValues = pallKey ? get(pallKey) : [];

          if (pallValues.length) {
            body += `<p><strong>Decreased utilization: ${pallValues.join(', ')}</strong></p>`;
          }

          const flexValues = flexKey ? get(flexKey) : [];

          if (flexValues.length) {
            body += `<p><strong>Flexibility: ${flexValues.join(', ')}</strong></p>`;
          }

          // Map key order isn't guaranteed: skip the details textbox, whose key
          // also contains this id, or the Yes/No answer is never read.
          const ncId = Object.keys(qa).find(
            k => k.includes('interimSymptomsGeneralNewSymptoms') && !k.includes('TextBox')
          );
          const ncTextId = Object.keys(qa).find(k =>
            k.includes('interimSymptomsGeneralNewSymptomsTextBox')
          );
          const nc = ncId ? get(ncId)[0] : undefined;
          const ncText = ncTextId ? get(ncTextId)[0] || '' : '';

          if (typeof nc !== 'undefined') {
            if (nc === 'Yes') {
              globalInterimNewComplaintValue = 'Yes';

              if (ncText && !globalInterimNewComplaintDetails) {
                globalInterimNewComplaintDetails = ncText;
              }
            } else if (nc === 'No') {
              if (globalInterimNewComplaintValue !== 'Yes') {
                globalInterimNewComplaintValue = 'No';
              }
            } else if (nc && ncId && typeof globalInterimNewComplaintValue === 'undefined') {
              // Relabeled options: keep the answer instead of dropping it.
              globalInterimNewComplaintValue = get(ncId).join(', ');
            }
          }

          // Collect shared exercise questions
          const exerciseFreqId = Object.keys(qa).find(k =>
            k.includes('interimExerciseADLFrequency')
          );
          const exerciseImpId = Object.keys(qa).find(k =>
            k.includes('interimExerciseADLImpediments')
          );
          const exerciseADLStartedId = Object.keys(qa).find(k =>
            k.includes('interimExerciseADLStartedADL')
          );

          if (exerciseFreqId && !globalInterimExerciseFrequency) {
            globalInterimExerciseFrequency = get(exerciseFreqId).join(', ') || undefined;
          }

          if (exerciseImpId) {
            const impediments = get(exerciseImpId);

            if (impediments.length && globalInterimExerciseImpediments.length === 0) {
              globalInterimExerciseImpediments = impediments;
            }
          }

          if (exerciseADLStartedId && !globalInterimADLImplementation) {
            globalInterimADLImplementation = get(exerciseADLStartedId).join(', ') || undefined;
          }

          // Built-in interim ids all start with "interim", rendered above or
          // deliberately omitted (demographics). Shared entries repeat in every
          // complaint's map, so they render once at the end.
          body += buildExtraQuestionsSubjective(
            qa,
            useSecondary,
            id => id.startsWith('interim') || qa[id]?.shared === true
          );

          // Shared copies are identical across summaries; a summary whose
          // write failed yields '' and falls through to the next complaint.
          if (!globalInterimCustomExtras) {
            globalInterimCustomExtras = buildExtraQuestionsSubjective(
              qa,
              useSecondary,
              id => id.startsWith('interim') || qa[id]?.shared !== true
            );
          }
        } else if (summary.type === ComplaintSummaryType.Reeval) {
          const get = (id?: string) => getUnifiedValues(qa, id);
          const findKey = (baseId: string) => Object.keys(qa).find(k => k.includes(baseId));
          const statusKey = findKey('4fbff72a-d32a-433b-bf2d-92a941cd1344'); // reevalSymptomsQuestionId
          const improvementKey = findKey('37586d68-b15c-4c21-b2d3-0f0b87fd18b8'); // reevalSymptomsPercentImprovedQuestionId
          const spreadKey = findKey('e5504754-d0d8-429e-910e-b6456c091815'); // reevalsymptomsSpreadQuestionId
          const overview: string[] = [];
          // Custom surveys can change a built-in question's type or options, so
          // built-in formatting applies only to the answer shape it expects;
          // anything else prints the patient's selected labels.
          const isCustomSurvey = patientSurvey?.survey?.isCustom === true;
          const isNumber = (value?: string) =>
            value !== undefined && value !== '' && !isNaN(Number(value));

          if (statusKey) {
            overview.push(`<li>Status: <strong>${get(statusKey).join(', ')}</strong></li>`);
          }

          // Add improvement percentage if conditions are met
          if (improvementKey && statusKey) {
            const statusValue = get(statusKey)[0];
            const improvementValue = get(improvementKey)[0];
            // A relabeled status can't be matched to "Improved"; the answered
            // improvement question (asked only when its condition held) decides.
            const customStatus =
              isCustomSurvey &&
              !ccsOptionSets.reevalSymptoms.some(option => option.en === statusValue);

            if ((statusValue === 'Improved' || customStatus) && improvementValue) {
              overview.push(
                !isCustomSurvey || isNumber(improvementValue)
                  ? `<li>Patient reported improvement: <strong>${improvementValue}%</strong></li>`
                  : `<li>Patient reported improvement: <strong>${escapeHtml(get(improvementKey).join(', '))}</strong></li>`
              );
            }
          }

          if (spreadKey) {
            overview.push(
              `<li>Symptom Distribution: <strong>${get(spreadKey).join(', ')}</strong></li>`
            );
          }

          // New provider consultations - always show
          const providerKey =
            findKey(CCSPrimaryQuestionIds.providerQuestionId) ||
            findKey(CCSSecondaryQuestionIds.providerQuestionId);
          const providers = providerKey ? get(providerKey) : [];

          if (providers.length > 0 && providerKey) {
            // Get customInputData from the question answer if it exists
            const providerQA = qa[providerKey];
            const customInputData = providerQA?.customInputData || {};

            // Format each provider with its custom input if available
            const formattedProviders = providers.map((provider, index) => {
              // customInputData is keyed by the selected option id, while
              // `providers` contains the clinician-facing display text.
              const selectedProviderId = providerQA?.selectedValues?.[index] || provider;
              const customInput = customInputData[selectedProviderId];

              return customInput ? `${provider}: "${customInput}"` : provider;
            });

            overview.push(
              `<li>New provider consultations: Since the last evaluation, the patient reports consulting <strong>${formattedProviders.join(', ')}</strong></li>`
            );
          } else {
            overview.push(
              `<li>New provider consultations: Since the last evaluation, the patient denies consulting other practitioners.</li>`
            );
          }

          if (overview.length) {
            body += `<p><span style="font-weight: bold;">Overview</span></p><ul>${overview.join('')}</ul>`;
          }

          // Built-in re-evals use the secondary CCS ids; custom re-eval imports
          // retain the primary ids. Preserve the built-in lookup and fall back
          // to the custom-template id so the no-template SOAP path renders both.
          const provKey =
            findKey(CCSSecondaryQuestionIds.aggravatesQuestionId) ||
            findKey(CCSPrimaryQuestionIds.aggravatesQuestionId);
          const prov = provKey ? get(provKey) : [];

          if (prov.length) {
            body += `<p><span style="font-weight: bold;">${SectionTitles.ProvocativeFactors}</span></p><ul>${prov.map(v => `<li><strong>${v}</strong></li>`).join('')}</ul>`;
          }

          const pallKey =
            findKey(CCSSecondaryQuestionIds.reliefQuestionId) ||
            findKey(CCSPrimaryQuestionIds.reliefQuestionId);
          const pall = pallKey ? get(pallKey) : [];

          if (pall.length) {
            body += `<p><span style="font-weight: bold;">${SectionTitles.PalliativeFactors}</span></p><ul>${pall.map(v => `<li><strong>${v}</strong></li>`).join('')}</ul>`;
          }

          // Quality section for reeval
          const mainKey =
            findKey(CCSPrimaryQuestionIds.mainQuestionId) ||
            findKey(CCSSecondaryQuestionIds.mainQuestionId);
          const mainValues = mainKey ? get(mainKey) : [];

          const lisQuality: string[] = [];

          const pushDesc = (label: string, descIdBase: string, secondaryDescIdBase?: string) => {
            if (!mainValues.includes(label)) return;
            const descId =
              findKey(descIdBase) ||
              (secondaryDescIdBase ? findKey(secondaryDescIdBase) : undefined);
            const desc = descId ? get(descId) : [];

            if (desc.length) {
              lisQuality.push(
                `<li><strong>${label}</strong> <span>described as </span><strong>${desc.join(', ')}</strong></li>`
              );
            } else {
              lisQuality.push(`<li><strong>${label}</strong></li>`);
            }
          };

          pushDesc(
            'Pain / Discomfort',
            CCSPrimaryQuestionIds.bestDescribesQuestionPainId,
            CCSSecondaryQuestionIds.bestDescribesQuestionPainId
          );
          pushDesc(
            'Altered sensation',
            CCSPrimaryQuestionIds.bestDescribesQuestionAlteredId,
            CCSSecondaryQuestionIds.bestDescribesQuestionAlteredId
          );
          pushDesc(
            'Weakness',
            CCSPrimaryQuestionIds.bestDescribesQuestionWeaknessId,
            CCSSecondaryQuestionIds.bestDescribesQuestionWeaknessId
          );

          if (mainValues.includes('Noise / Grinding')) {
            lisQuality.push('<li><strong>Noise / Grinding</strong></li>');
          }

          if (mainValues.includes('Stiffness / Tightness')) {
            lisQuality.push('<li><strong>Stiffness / Tightness</strong></li>');
          }

          // Custom surveys may relabel or add symptom options; list those as
          // chosen. Built-in output is unchanged (its extras stay omitted).
          if (isCustomSurvey) {
            mainValues
              .filter(value => !ccsOptionSets.describe.some(option => option.en === value))
              .forEach(value => lisQuality.push(`<li><strong>${escapeHtml(value)}</strong></li>`));
          }

          if (lisQuality.length) {
            body += `<p><span style="font-weight: bold;">${SectionTitles.Quality}</span></p><ul>${lisQuality.join('')}</ul>`;
          }

          const avgKey =
            findKey(CCSSecondaryQuestionIds.intensityQuestionId) ||
            findKey(CCSPrimaryQuestionIds.intensityQuestionId);
          const worstKey =
            findKey(CCSSecondaryQuestionIds.intensity2QuestionId) ||
            findKey(CCSPrimaryQuestionIds.intensity2QuestionId);
          const enjoyKey =
            findKey(CCSSecondaryQuestionIds.interferedQuestionId) ||
            findKey(CCSPrimaryQuestionIds.interferedQuestionId);
          const gaKey =
            findKey(CCSSecondaryQuestionIds.generalInterferedQuestionId) ||
            findKey(CCSPrimaryQuestionIds.generalInterferedQuestionId);

          const avg = avgKey ? get(avgKey)[0] : undefined;
          const worst = worstKey ? get(worstKey)[0] : undefined;
          const enjoy = enjoyKey ? get(enjoyKey)[0] : '';
          const ga = gaKey ? get(gaKey)[0] : '';
          const sevLis: string[] = [];
          // Labeled (non-numeric) answers print as chosen, without "/10".
          const labels = (key?: string) => escapeHtml(get(key).join(', '));

          // Check if "Pain / Discomfort" is selected in main quality question (already defined above)
          const hasPainDiscomfort = mainValues.includes('Pain / Discomfort');
          // A relabeled Pain option can't match by label; the survey's own
          // condition (checked against option ids) decides instead.
          const painGate = (key?: string) =>
            hasPainDiscomfort || (isCustomSurvey && !!key && qaConditionsMet(key, qa));

          // VAS questions are conditional on "Pain / Discomfort" being selected
          if (hasPainDiscomfort && !isNaN(Number(avg))) {
            sevLis.push(`<li>Pain severity: VAS average <strong>${avg}</strong>/10</li>`);
          } else if (isCustomSurvey && avg && !isNumber(avg) && painGate(avgKey)) {
            sevLis.push(`<li>Pain severity: VAS average <strong>${labels(avgKey)}</strong></li>`);
          } else if (isCustomSurvey && !hasPainDiscomfort && isNumber(avg) && painGate(avgKey)) {
            sevLis.push(`<li>Pain severity: VAS average <strong>${avg}</strong>/10</li>`);
          }

          if (hasPainDiscomfort && !isNaN(Number(worst))) {
            sevLis.push(`<li>Pain severity: VAS high <strong>${worst}</strong>/10</li>`);
          } else if (isCustomSurvey && worst && !isNumber(worst) && painGate(worstKey)) {
            sevLis.push(`<li>Pain severity: VAS high <strong>${labels(worstKey)}</strong></li>`);
          } else if (
            isCustomSurvey &&
            !hasPainDiscomfort &&
            isNumber(worst) &&
            painGate(worstKey)
          ) {
            sevLis.push(`<li>Pain severity: VAS high <strong>${worst}</strong>/10</li>`);
          }

          if (enjoy) {
            sevLis.push(
              !isCustomSurvey || isNumber(enjoy)
                ? `<li>Life enjoyment interference: <strong>${enjoy}</strong>/10</li>`
                : `<li>Life enjoyment interference: <strong>${labels(enjoyKey)}</strong></li>`
            );
          }

          if (ga) {
            sevLis.push(
              !isCustomSurvey || isNumber(ga)
                ? `<li>General activity interference: <strong>${ga}</strong>/10</li>`
                : `<li>General activity interference: <strong>${labels(gaKey)}</strong></li>`
            );
          }

          if (sevLis.length) {
            body += `<p><span style="font-weight: bold;">${SectionTitles.Severity}</span></p><ul>${sevLis.join('')}</ul>`;
          }

          const daysKey =
            findKey(CCSSecondaryQuestionIds.percentOfDaysQuestionId) ||
            findKey(CCSPrimaryQuestionIds.percentOfDaysQuestionId);
          const eachKey =
            findKey(CCSSecondaryQuestionIds.symptomsArePresentQuestionId) ||
            findKey(CCSPrimaryQuestionIds.symptomsArePresentQuestionId);
          const days = daysKey ? Number(get(daysKey)[0] || '') : NaN;
          const each = eachKey ? Number(get(eachKey)[0] || '') : NaN;
          const timParts: string[] = [];

          if (isCustomSurvey && daysKey && !isFinite(days) && get(daysKey).length) {
            timParts.push(`Symptoms present <strong>${labels(daysKey)}</strong>`);
          }

          if (isCustomSurvey && eachKey && !isFinite(each) && get(eachKey).length) {
            timParts.push(`Symptoms present each day <strong>${labels(eachKey)}</strong>`);
          }

          if (isFinite(days)) {
            timParts.push(
              `Symptoms present <strong>${timingDescriptor(days, 'days')}</strong> (${days}% of days)`
            );
          }

          if (isFinite(each)) {
            timParts.push(
              `<strong>${timingDescriptor(each, 'eachDay')}</strong> each day (${each}% of the day)`
            );
          }

          if (timParts.length) {
            body += `<p><span style="font-weight: bold;">${SectionTitles.Timing}</span></p><ul><li>${timParts.join(', ')}</li></ul>`;
          }
        }

        if (complaint?.subjective) {
          body += `\n<p><strong>Note:</strong></p>\n<ul><li>${complaint.subjective}</li></ul>`;
        }

        return acc + header + body;
      }, '');

      const pregnancySection = renderPregnancyHtml({
        statuses: Array.from(pregnancyStatuses),
        dueDates: Array.from(pregnancyDueDates),
        lmpDates: Array.from(pregnancyLmpDates),
      });
      const yellowFlagsSection = renderYellowFlagsHtml(allYellowFlags);

      let finalHtml = unifiedHtml + (pregnancySection || '') + (yellowFlagsSection || '');

      if (globalInterimExerciseFrequency) {
        finalHtml += buildSharedExerciseBlock({
          frequency: globalInterimExerciseFrequency,
          impediments: globalInterimExerciseImpediments,
          adlImplementation: globalInterimADLImplementation,
        });
      }

      if (typeof globalInterimNewComplaintValue !== 'undefined') {
        finalHtml += buildSharedInterimBlock({
          newComplaintValue: globalInterimNewComplaintValue,
          newComplaintDetails: globalInterimNewComplaintDetails,
        });
      }

      if (globalInterimCustomExtras) {
        finalHtml += globalInterimCustomExtras;
      }

      return finalHtml;
    }

    // Build the content for each complaint
    const ccsInfo: CcsInfoArea[] = [];

    orderedComplaints.forEach(complaint => {
      const complaintId = complaint.id;
      if (!complaintId) return;

      // Check if we have a summary for this complaint
      const complaintSummary = complaintSummaries?.[complaintId];

      if (complaintSummary && complaintSummary.type) {
        // Fallback path retained for legacy summaries with sections is removed; unified path handles modern data
        // If type exists but unified not present, show header only
        ccsInfo.push({
          id: complaintId,
          problem: complaintNames?.[complaintId]?.name || complaint.complaint || '',
          sections: [],
          problemArea: complaint?.problemArea,
        });
      } else {
        // Fallback: try to build from complaint data
        const problem = (
          complaint?.bodypicker ? createName(complaint?.bodypicker.parts) : complaint.complaint
        ) as string;

        ccsInfo.push({
          id: complaintId,
          problem,
          // If summary exists but is not initial, skip CCS-specific content
          // and render no sections for this complaint here.
          sections: [],
          problemArea: complaint?.problemArea,
        });
      }
    });

    // Generate the final content using the same function as V1
    const v2Result = WYSIWYGContent({
      complaints: orderedComplaints,
      ccsInfo,
      totalComplaints: finalTotalComplaints,
    });

    return v2Result;
  };
};

// Helpers to build subjective strings directly from unified complaintRelatedQuestions
function getUnifiedValues(qaMap: Record<string, QuestionAnswer>, id?: string): string[] {
  if (!id) return [];
  const qa = qaMap[id];
  if (!qa || !Array.isArray(qa.selectedValues)) return [];

  const optionDisplays = new Map(
    (qa.options || []).map(option => [String(option.id), String(option.display)] as const)
  );

  return qa.selectedValues.map(value => {
    if (value === 'other' && qa.otherValue) return qa.otherValue;

    return optionDisplays.get(String(value)) ?? String(value);
  });
}

function timingDescriptor(percent: number, which: 'days' | 'eachDay'): string {
  if (which === 'days') {
    return percent < 20
      ? 'rarely'
      : percent < 40
        ? 'moderately'
        : percent < 60
          ? 'frequently'
          : 'continually';
  }

  return percent < 20
    ? 'infrequently'
    : percent < 40
      ? 'moderately'
      : percent < 60
        ? 'frequently'
        : 'continually';
}

function formatOnsetDate(value?: string): string {
  if (!value) return '';

  const trimmed = value.trim();
  if (!trimmed) return '';

  const lower = trimmed.toLowerCase();

  // Preserve relative dates (e.g., "3 days ago", "yesterday")
  if (lower.includes('ago')) return trimmed;

  // Try to parse as a date
  let parsedDate = dayjs(trimmed);

  if (!parsedDate.isValid()) {
    // Attempt to parse common MM/DD/YYYY format
    const mmddyyyyMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

    if (mmddyyyyMatch) {
      parsedDate = dayjs(`${mmddyyyyMatch[3]}-${mmddyyyyMatch[1]}-${mmddyyyyMatch[2]}`);
    }
  }

  if (!parsedDate.isValid()) {
    // Final fallback to native Date parsing
    const nativeDate = new Date(trimmed);

    if (!isNaN(nativeDate.getTime())) {
      parsedDate = dayjs(nativeDate);
    }
  }

  // Return formatted date if valid, otherwise return original input
  return parsedDate.isValid() ? parsedDate.format('MM/DD/YYYY') : trimmed;
}

/**
 * Check if a question's conditional visibility (if/ifSome) is met based on the QA map.
 * The QA map is actually Record<string, UnifiedQAEntry> at runtime, so it has if/ifSome arrays.
 */
function qaConditionsMet(questionId: string, qa: Record<string, QuestionAnswer>): boolean {
  const raw = qa[questionId] as unknown as {
    if?: Array<{ id?: string; value?: unknown }>;
    ifSome?: Array<{ id?: string; value?: unknown }>;
  };

  if (!raw) return false;

  const meetsOne = (cond?: { id?: string; value?: unknown }): boolean => {
    if (!cond?.id) return true;
    const selected = qa[cond.id]?.selectedValues || [];
    if (cond.value === null || cond.value === 'null') return selected.length > 0;

    if (Array.isArray(cond.value)) {
      return selected.some(v => (cond.value as (string | number | boolean)[]).includes(v));
    }

    return selected.includes(cond.value as string);
  };

  const ifOk = Array.isArray(raw.if) ? raw.if.every(c => meetsOne(c)) : true;
  const ifSomeOk = Array.isArray(raw.ifSome) ? raw.ifSome.some(c => meetsOne(c)) : true;

  return ifOk && ifSomeOk;
}

/**
 * Map selectedValues to display text using the options array when id !== display.
 */
function resolveDisplayValues(entry: QuestionAnswer): string {
  const optMap = entry.options?.length
    ? new Map(entry.options.map(o => [o.id, o.display]))
    : undefined;

  return entry.selectedValues
    .map(v => {
      if (v === 'other' && entry.otherValue) return entry.otherValue;

      const disp = optMap?.get(v);
      if (disp && disp !== v) return disp;

      return v;
    })
    .filter(Boolean)
    .join(', ');
}

/**
 * Build subjective HTML for any QA entries not covered by standard CCS question IDs.
 * Appended after the standard CCS output for hybrid surveys.
 */
// Render a group question for the subjective note: bold group label, then the
// answered sub-questions indented beneath it as a list. A list (not margin-left
// on <p>) is used so the indentation survives into the TipTap editor and matches
// how the other sections in this builder indent their content.
// Returns '' when no sub-question has an answer.
// Shared by both subjective Q&A builders so group handling can't diverge.
export function renderGroupSubjectiveHtml(entry: QuestionAnswer): string {
  const subItems: string[] = [];

  for (const sub of entry.groupSubAnswers || []) {
    if (!sub?.questionText || !sub?.selectedValues?.length) continue;
    const subValues = resolveDisplayValues(sub);
    const subLabel = sub.questionText.replace(/:\s*$/, '');

    if (subValues) {
      // subValues can carry patient-typed text (Other / free-text answers) and
      // this HTML lands in the TipTap note editor — escape it.
      subItems.push(`<li><strong>${subLabel}:</strong> ${escapeHtml(subValues)}</li>`);
    }
  }

  if (!subItems.length) return '';

  const groupLabel = (entry.questionText || '').replace(/:\s*$/, '');

  return (
    (groupLabel ? `<p><strong>${groupLabel}</strong></p>` : '') + `<ul>${subItems.join('')}</ul>`
  );
}

function buildExtraQuestionsSubjective(
  qa: Record<string, QuestionAnswer>,
  useSecondary: boolean,
  skipQuestionId?: (id: string) => boolean
): string {
  const Q = (useSecondary ? CCSSecondaryQuestionIds : CCSPrimaryQuestionIds) as Record<
    string,
    string
  >;
  const knownIds = new Set(Object.values(Q));

  // Also include abbreviated CCS IDs
  for (const id of Object.values(abbreviatedCCSQuestionsIds)) {
    knownIds.add(id);
  }

  // Exclude "Do you have any other symptoms?" questions (navigation-only, not complaint-specific)
  knownIds.add('23b575bd-37fd-4624-ae3c-a27664d13eab'); // CCS primary "more complaints"
  knownIds.add('374cc214-6ba2-4b50-875a-d6268e8e39e5'); // CCS secondary "more complaints"
  knownIds.add('more-complaints'); // templateAlias for custom surveys

  let html = '';

  // DynamoDB maps don't preserve key order — sort by stamped survey position
  // (legacy rows without `order` keep map order, last).
  const orderedEntries = Object.entries(qa || {}).sort(
    ([, a], [, b]) => (a?.order ?? Number.MAX_SAFE_INTEGER) - (b?.order ?? Number.MAX_SAFE_INTEGER)
  );

  for (const [questionId, entry] of orderedEntries) {
    if (knownIds.has(questionId)) continue;
    if (skipQuestionId?.(questionId)) continue;
    if (!qaConditionsMet(questionId, qa)) continue;

    // Group questions: group label as header, then each sub-answer.
    if (entry.questionType === 'group') {
      html += renderGroupSubjectiveHtml(entry);
      continue;
    }

    if (!entry?.questionText || !entry?.selectedValues?.length) continue;
    if (entry.questionType === 'bodypicker') continue;

    const values = resolveDisplayValues(entry);
    const label = entry.questionText.replace(/:\s*$/, '');

    if (values) {
      // values can carry patient-typed text (Other / free-text answers) and
      // this HTML lands in the TipTap note editor — escape it.
      html += `<p><strong>${label}:</strong> ${escapeHtml(values)}</p>`;
    }
  }

  return html;
}

/**
 * Convert processed template text to HTML with proper structure:
 * - `## Title` lines → bold section header `<p><span style="font-weight: bold;">Title</span></p>`
 * - `- content` lines → grouped into `<ul><li>content</li>...</ul>`
 * - Blank lines → skip (section separator)
 * - Other lines → `<p>line</p>` (backward compat)
 * Post-process: remove orphaned headers (header with no content following).
 */
function renderTemplateLines(text: string): string {
  const lines = text.split('\n');
  const htmlParts: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (line === '') {
      // Skip blank lines (section separators)
      i++;
      continue;
    }

    if (line.startsWith('## ')) {
      // Section header
      const title = line.slice(3).trim();
      htmlParts.push(`<p><span style="font-weight: bold;">${title}</span></p>`);
      i++;
      continue;
    }

    if (line.startsWith('- ')) {
      // Collect consecutive bullet lines into a <ul>
      const lis: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('- ')) {
        lis.push(`<li>${lines[i].trim().slice(2).trim()}</li>`);
        i++;
      }
      htmlParts.push(`<ul>${lis.join('')}</ul>`);
      continue;
    }

    // Regular text line
    htmlParts.push(`<p>${line}</p>`);
    i++;
  }

  // Post-process: remove orphaned headers (header followed by nothing or another header)
  const filtered: string[] = [];
  for (let j = 0; j < htmlParts.length; j++) {
    const isHeader = htmlParts[j].startsWith('<p><span style="font-weight: bold;">');
    if (isHeader) {
      // Check if next part is content (not a header and not end of array)
      const next = htmlParts[j + 1];
      if (!next || next.startsWith('<p><span style="font-weight: bold;">')) {
        // Orphaned header — skip it
        continue;
      }
    }
    filtered.push(htmlParts[j]);
  }

  return filtered.join('');
}

/**
 * Matches placeholder syntax left unresolved after template processing so the
 * renderer can fall back to the Q&A format instead of emitting raw tokens into
 * the note. Covers everything buildSubjectiveFromQAMap resolves: {aN}, {qN},
 * {#aN}, {id:...}, {qid:...}, {#id:...}, {!id:...}, and {title}. Substituted
 * values have their braces HTML-encoded, so patient-typed text can't
 * false-positive this check.
 */
const UNRESOLVED_TEMPLATE_TOKEN = /\{[#/!]?(?:[aq]\d+\}|q?id:[\w-]+[^}]*\}|title\})/i;

/**
 * Build subjective HTML for a custom CCS complaint.
 * Uses subjectiveTemplate if available, otherwise falls back to Q&A format.
 */
function buildCustomCCSSubjective(
  qa: Record<string, QuestionAnswer>,
  subjectiveTemplate?: string,
  aliasMap?: Record<string, string>,
  patientSurvey?: PatientSurvey
): string {
  if (subjectiveTemplate) {
    const text = buildSubjectiveFromQAMap(subjectiveTemplate, qa, aliasMap, patientSurvey);
    if (!text || UNRESOLVED_TEMPLATE_TOKEN.test(text)) {
      // Template produced nothing or left raw placeholder tokens —
      // fall through to Q&A fallback instead of rendering them into the note
    } else {
      // Check if template uses structured syntax (## headers or - bullets)
      const hasStructuredSyntax = text
        .split('\n')
        .some(line => line.trim().startsWith('## ') || line.trim().startsWith('- '));

      if (hasStructuredSyntax) {
        return renderTemplateLines(text);
      }

      // Legacy: Wrap each line in its own <p> tag.
      // Using <br /> won't survive the collaborative editor's stripTags.br() pass,
      // but <p> opening tags are preserved and TipTap treats them as paragraph breaks.
      // Blank lines use &nbsp; so they survive the stripTags.p empty-paragraph removal.
      return text
        .split('\n')
        .map(line => (line.trim() === '' ? '<p>&nbsp;</p>' : `<p>${line}</p>`))
        .join('');
    }
  }

  // Fallback: Q&A format
  let html = '';

  for (const [questionId, entry] of Object.entries(qa || {})) {
    if (!qaConditionsMet(questionId, qa)) continue;

    // Group questions: group label as header, then each sub-answer.
    if (entry.questionType === 'group') {
      html += renderGroupSubjectiveHtml(entry);
      continue;
    }

    if (!entry?.questionText || !entry?.selectedValues?.length) continue;
    if (entry.questionType === 'bodypicker') continue;

    const values = resolveDisplayValues(entry);
    const label = entry.questionText.replace(/:\s*$/, '');

    if (values) {
      // values can carry patient-typed text (Other / free-text answers) and
      // this HTML lands in the TipTap note editor — escape it.
      html += `<p><strong>${label}:</strong> ${escapeHtml(values)}</p>`;
    }
  }

  return html;
}

/**
 * Apply pipe transforms to a resolved answer string.
 * The special 'bullets' pipe is handled at the call site (not here) because
 * it needs access to the raw selectedValues array before they are joined.
 */
function applyQAPipes(value: string, pipes: string): string {
  let result = value;

  for (const pipe of pipes.split('|').filter(Boolean)) {
    if (pipe === 'lower') {
      result = result.toLowerCase();
    } else if (pipe.startsWith('yesno:')) {
      const parts = pipe.split(':');
      const yesText = parts[1] || '';
      const noText = parts[2] || '';
      result = result === 'Yes' ? yesText : noText;
    } else if (pipe === 'percent_desc') {
      const n = Number(result);

      if (isNaN(n)) {
        result = '';
      } else {
        result = n < 20 ? 'rarely' : n < 40 ? 'moderately' : n < 60 ? 'frequently' : 'continually';
      }
    } else if (pipe.startsWith('join:')) {
      const sep = pipe.slice(5);
      result = result.split(', ').join(sep);
    }
    // 'date' is a passthrough — already formatted
    // 'bullets' is handled at the call site
  }

  return result;
}

/**
 * Escape a substituted answer value for template output. Braces are
 * HTML-encoded so patient-typed text can't mimic template tokens and trip
 * UNRESOLVED_TEMPLATE_TOKEN into discarding the whole template.
 */
const escapeTemplateValue = (value: string): string =>
  escapeHtml(value).replace(/\{/g, '&#123;').replace(/\}/g, '&#125;');

/**
 * Process a subjectiveTemplate against a QA map.
 * Resolves {id:UUID} placeholders and {#id:UUID}...{/id:UUID} conditional blocks.
 * When aliasMap is provided, alias identifiers (e.g. "symptoms-begin") are resolved
 * to UUIDs before looking up the QA entry, so both formats work.
 *
 * Processing order: conditionals first (so empty answers cause block removal),
 * then value replacements with pipe transforms.
 */
function buildSubjectiveFromQAMap(
  template: string,
  qa: Record<string, QuestionAnswer>,
  aliasMap?: Record<string, string>,
  patientSurvey?: PatientSurvey
): string {
  /** Find a QA entry by question UUID, descending into group sub-answers. */
  const findEntryById = (id: string): QuestionAnswer | undefined => {
    // Map keys are the question UUID (reeval keys may carry a suffix past char 36)
    const direct = qa[id] || qa[String(id).slice(0, 36)];
    if (direct) return direct;

    for (const entry of Object.values(qa)) {
      const sub = entry.groupSubAnswers?.find(s => s.id === id);
      if (sub) return sub;
    }

    return undefined;
  };

  /** Resolve an id (which may be a UUID or a friendly alias) to its QA entry. */
  const resolveQA = (id: string): QuestionAnswer | undefined => {
    const direct = findEntryById(id);
    if (direct) return direct;

    if (aliasMap) {
      const uuid = aliasMap[id];
      if (uuid) return findEntryById(uuid);
    }

    return undefined;
  };

  // Index placeholders ({aN}/{qN}/{#aN}) are positional against the survey's
  // flattened question list (groups expand to sub-questions), matching the
  // standalone engine in buildCustomSurveySoapLines. The QA map can't provide
  // the ordering: it omits bodypickers and stores groups as single entries.
  const flatQuestions = patientSurvey?.survey?.questions
    ? flattenQuestions(patientSurvey.survey.questions)
    : undefined;

  const resolveQAByIndex = (oneBased: number): QuestionAnswer | undefined => {
    const question = flatQuestions?.[oneBased - 1];
    if (!question?.id) return undefined;

    return findEntryById(question.id);
  };

  const questionTextByIndex = (oneBased: number): string => {
    const entry = resolveQAByIndex(oneBased);
    if (entry?.questionText) return entry.questionText.replace(/<[^>]*>/g, '');

    const question = flatQuestions?.[oneBased - 1];

    return (question?.question || '').replace(/<[^>]*>/g, '');
  };

  let result = template;

  // Process all conditional blocks in a loop so that nested conditionals
  // (e.g. {#id:A}{#id:B}...{/id:B}{/id:A}) are resolved from outside-in.
  // Each pass resolves one nesting level; loop until stable.
  const MAX_PASSES = 5;
  for (let pass = 0; pass < MAX_PASSES; pass++) {
    const before = result;

    // 0a. Process includes-conditional blocks {#id:UUID~value}...{/id:UUID}
    // For multi-select questions, checks if selectedValues includes the given value.
    result = result.replace(
      /\{#id:([\w-]+)~([^}]+)\}([\s\S]*?)\{\/id:\1\}/gi,
      (_match, id, expectedValue, inner) => {
        const entry = resolveQA(id);
        if (!entry?.selectedValues?.length) return '';
        // Check raw selectedValues and also display-resolved values
        const rawIncludes = entry.selectedValues.some(
          v => String(v).toLowerCase() === expectedValue.toLowerCase()
        );
        if (!rawIncludes) {
          // Also check display values
          const optMap = entry.options?.length
            ? new Map(entry.options.map(o => [o.id, o.display]))
            : undefined;
          const displayIncludes = entry.selectedValues.some(v => {
            const disp = optMap?.get(v);
            return disp && disp.toLowerCase() === expectedValue.toLowerCase();
          });
          if (!displayIncludes) return '';
        }

        return inner;
      }
    );

    // 0b. Process negated conditional blocks {!id:UUID}...{/!id:UUID}
    // Renders content only when the question has NO selected values.
    result = result.replace(/\{!id:([\w-]+)\}([\s\S]*?)\{\/!id:\1\}/gi, (_match, id, inner) => {
      const entry = resolveQA(id);
      if (entry?.selectedValues?.length) return '';

      return inner;
    });

    // 0c. Process index-based conditional blocks {#aN}...{/aN}
    // Only when the survey is available for positional resolution; otherwise
    // the tokens stay in place and the caller falls back to the Q&A format.
    if (flatQuestions) {
      result = result.replace(/\{#a(\d+)\}([\s\S]*?)\{\/a\1\}/g, (_match, numStr, inner) => {
        const entry = resolveQAByIndex(parseInt(numStr, 10));
        if (!entry?.selectedValues?.length) return '';

        return inner;
      });
    }

    // 1. Process value-conditional blocks {#id:UUID=val}...{/id:UUID}
    result = result.replace(
      /\{#id:([\w-]+)=([^}]+)\}([\s\S]*?)\{\/id:\1\}/gi,
      (_match, id, expectedValue, inner) => {
        const entry = resolveQA(id);
        if (!entry?.selectedValues?.length) return '';
        const display = resolveDisplayValues(entry);
        if (display.toLowerCase() !== expectedValue.toLowerCase()) return '';

        return inner;
      }
    );

    // 2. Process conditional blocks {#id:UUID}...{/id:UUID}
    result = result.replace(/\{#id:([\w-]+)\}([\s\S]*?)\{\/id:\1\}/gi, (_match, id, inner) => {
      const entry = resolveQA(id);
      if (!entry?.selectedValues?.length) return '';

      return inner;
    });

    // If nothing changed this pass, all conditionals are resolved
    if (result === before) break;
  }

  // 2a. Replace {title} — survey name. Replaced (even when the name is blank)
  // whenever the survey is known, so a nameless survey can't strand the token
  // and needlessly trip the unresolved-token fallback.
  if (patientSurvey) {
    const surveyName = patientSurvey?.survey?.english?.name || patientSurvey?.survey?.name || '';
    result = result.replace(/\{title\}/g, escapeTemplateValue(surveyName));
  }

  // 2a'. Replace {qid:UUID} — question text by ID (parity with the standalone
  // engine in buildCustomSurveySoapLines and the survey editor preview).
  result = result.replace(/\{qid:([\w-]+)\}/gi, (_match, id) => {
    const entry = resolveQA(id);
    if (entry?.questionText) return entry.questionText.replace(/<[^>]*>/g, '');

    const question = flatQuestions?.find(q => String(q.id) === String(id));

    return (question?.question || '').replace(/<[^>]*>/g, '');
  });

  // 2b. Replace {qN} and {aN} — question text / answer value by flattened index.
  // Skipped without the survey so the tokens survive to the fallback check.
  if (flatQuestions) {
    result = result.replace(/\{q(\d+)\}/g, (_match, numStr) =>
      questionTextByIndex(parseInt(numStr, 10))
    );

    result = result.replace(/\{a(\d+)\}/g, (_match, numStr) => {
      const entry = resolveQAByIndex(parseInt(numStr, 10));
      if (!entry?.selectedValues?.length) return '';

      return escapeTemplateValue(resolveDisplayValues(entry));
    });
  }

  // 3. Replace {id:UUID|pipes} and {id:UUID} with resolved answer values
  // Special handling for |bullets pipe: produce one "- value" line per selected value.
  result = result.replace(/\{id:([\w-]+)(?:\|([^}]+))?\}/gi, (_match, id, pipes) => {
    const entry = resolveQA(id);
    if (!entry?.selectedValues?.length) return '';

    // Substituted answer values are escaped because the resolved template text
    // is always converted to HTML for the TipTap note editor (renderTemplateLines
    // or per-line <p> wrapping) and can carry patient-typed free text. The
    // clinician-authored template text around the placeholders stays raw.
    if (pipes && pipes.split('|').includes('bullets')) {
      // Produce one "- value" line per selected value
      const optMap = entry.options?.length
        ? new Map(entry.options.map(o => [o.id, o.display]))
        : undefined;
      return entry.selectedValues
        .map(v => {
          if (v === 'other' && entry.otherValue) {
            return `- ${escapeTemplateValue(entry.otherValue)}`;
          }
          const disp = optMap?.get(v);
          return `- ${escapeTemplateValue(disp && disp !== v ? disp : String(v))}`;
        })
        .filter(Boolean)
        .join('\n');
    }

    const display = resolveDisplayValues(entry);

    return escapeTemplateValue(pipes ? applyQAPipes(display, pipes) : display);
  });

  // 4. Collapse horizontal spaces per line, remove empty lines from unresolved conditionals
  result = result
    .split('\n')
    .map(line => line.replace(/ {2,}/g, ' ').trim())
    .filter((line, i, arr) => {
      if (line === '' && i > 0 && arr[i - 1] === '') return false;

      return true;
    })
    .join('\n')
    .trim();

  return result;
}

function buildInitialSubjectiveFromUnified(
  qa: Record<string, QuestionAnswer>,
  useSecondary: boolean,
  accumulators: {
    pregnancyStatuses: Set<string>;
    pregnancyDueDates: Set<string>;
    pregnancyLmpDates: Set<string>;
    allYellowFlags: string[];
  }
): string {
  const Q = (useSecondary ? CCSSecondaryQuestionIds : CCSPrimaryQuestionIds) as Record<
    string,
    string
  >;

  const get = (id?: string) => getUnifiedValues(qa, id);
  const lisOnset: string[] = [];

  const onset = get(Q.symptomsBeginQuestionId)[0];
  const presentEpisode = get(Q.presentEpisodeQuestionId)[0];
  const dueTo = get(Q.causedQuestionId)[0];
  const accidentDateId = useSecondary
    ? CCSSecondaryQuestionIds.dateOfAccidentQuestionId
    : CCSPrimaryQuestionIds.dateOfAccidentQuestionId;
  const workInjuryDateId = useSecondary
    ? CCSSecondaryQuestionIds.whenDidInjuryQuestionId
    : CCSPrimaryQuestionIds.whenDidInjuryQuestionId;
  const accidentDate = accidentDateId ? get(accidentDateId)[0] : undefined;
  const workInjuryDate = workInjuryDateId ? get(workInjuryDateId)[0] : undefined;
  let when = presentEpisode;

  if (!when && dueTo === 'Auto accident' && accidentDate) {
    when = accidentDate;
  }

  if (!when && dueTo === 'Employment-related injury' && workInjuryDate) {
    when = workInjuryDate;
  }

  const formattedWhen = formatOnsetDate(when);

  if (onset || when || dueTo) {
    const partsOnset: string[] = [];

    // Beginning + onset (e.g., Abruptly/Gradual over time)
    if (onset) {
      partsOnset.push(`Beginning <strong>${onset}</strong>`);
    } else if (when || dueTo) {
      // If we have when or dueTo but no onset, still start the sentence
      partsOnset.push('Beginning');
    }

    // When (date/relative). If includes "ago" prefix with approximately. Always include "on" before explicit dates.

    if (formattedWhen) {
      const lowerWhen = formattedWhen.toLowerCase();
      const approximately = lowerWhen.includes('ago') ? 'approximately ' : '';
      const needsOn = !lowerWhen.includes('ago') && !!formattedWhen;
      const whenFragment = `${approximately}<strong>${formattedWhen}</strong>`.trim();
      partsOnset.push(`${needsOn ? 'on ' : ''}${whenFragment}`.trim());
    }

    // Cause
    if (dueTo) {
      partsOnset.push(`due to <strong>${dueTo}</strong>`);
    }

    const onsetLine = partsOnset.join(' ').replace(/\s+/g, ' ').trim();

    if (onsetLine) {
      lisOnset.push(`<li>${onsetLine}</li>`);
    }
  }

  const causeDesc = get(Q.brieflyDescribeQuestionId)[0];

  if (causeDesc && (dueTo === 'Single activity' || dueTo === 'Repetitive activity')) {
    lisOnset.push(`<li>Cause description: <strong>${causeDesc}</strong></li>`);
  }

  const prevSimilar = get(Q.sameSymptomsId)[0];

  if (prevSimilar) {
    lisOnset.push(`<li>Previous similar symptoms: <strong>${prevSimilar}</strong></li>`);

    if (prevSimilar === 'Yes') {
      const similarWhen = get(Q.estimateQuestionId)[0];

      if (similarWhen) {
        lisOnset.push(`<li>Similar symptoms began: <strong>${similarWhen}</strong></li>`);
      }
    }
  }

  const progression = get(Q.onsetQuestionId)[0];

  if (progression) {
    lisOnset.push(`<li>Symptoms <strong>${progression}</strong></li>`);
  }

  const onsetHtml = lisOnset.length
    ? `<p><span style="font-weight: bold;">${SectionTitles.Onset}</span></p><ul>${lisOnset.join('')}</ul>`
    : '';

  // Work injury / Auto accident details
  let injuryDetailsHtml = '';

  if (dueTo === 'Employment-related injury') {
    const workInjuryHtml = buildWorkInjuryDetailsHtml(qa);

    if (workInjuryHtml) {
      injuryDetailsHtml = `<span style="font-weight: bold;">Work injury details</span>${workInjuryHtml}`;
    }
  } else if (dueTo === 'Auto accident') {
    const autoAccidentHtml = buildAutoAccidentDetailsHtml(qa);

    if (autoAccidentHtml) {
      injuryDetailsHtml = `<span style="font-weight: bold;">Auto accident details</span>${autoAccidentHtml}`;
    }
  }

  // Quality
  const lisQuality: string[] = [];
  const main = get(Q.mainQuestionId);

  const pushDesc = (label: string, descId: string) => {
    const desc = get(descId);

    if (desc.length) {
      lisQuality.push(
        `<li><strong>${label}</strong> <span>described as </span><strong>${desc.join(', ')}</strong></li>`
      );
    } else {
      lisQuality.push(`<li><strong>${label}</strong></li>`);
    }
  };

  const mainLeft = main;
  let hasPainDiscomfort = false;

  if (main.includes('Pain / Discomfort')) {
    hasPainDiscomfort = true;
    pushDesc('Pain / Discomfort', Q.bestDescribesQuestionPainId);
    mainLeft.splice(mainLeft.indexOf('Pain / Discomfort'), 1);
  }

  if (main.includes('Altered sensation')) {
    pushDesc('Altered sensation', Q.bestDescribesQuestionAlteredId);
    mainLeft.splice(mainLeft.indexOf('Altered sensation'), 1);
  }

  if (main.includes('Weakness')) {
    pushDesc('Weakness', Q.bestDescribesQuestionWeaknessId);
    mainLeft.splice(mainLeft.indexOf('Weakness'), 1);
  }

  if (main.includes('Noise / Grinding')) {
    lisQuality.push('<li><strong>Noise / Grinding</strong></li>');
    mainLeft.splice(mainLeft.indexOf('Noise / Grinding'), 1);
  }

  if (main.includes('Stiffness / Tightness')) {
    lisQuality.push('<li><strong>Stiffness / Tightness</strong></li>');
    mainLeft.splice(mainLeft.indexOf('Stiffness / Tightness'), 1);
  }

  if (mainLeft.length) {
    for (const mainValue of mainLeft) {
      lisQuality.push(`<li><strong>${mainValue}</strong></li>`);
    }
  }

  // Abbreviated survey specific questions - only show if they exist and have answers
  // "Since the last visit, are your symptoms:" question
  const sinceLastVisitKey = Object.keys(qa).find(id =>
    qa[id]?.questionText?.toLowerCase().includes('since the last visit')
  );

  if (sinceLastVisitKey) {
    const sinceLastVisitValue = get(sinceLastVisitKey)[0];

    if (sinceLastVisitValue) {
      lisQuality.push(
        `<li>Since last visit symptoms are: <strong>${sinceLastVisitValue}</strong></li>`
      );
    }
  }

  // // "Average symptom severity" question (abbreviated survey specific)
  // const avgSeverityKey = Object.keys(qa).find(
  //   id =>
  //     qa[id]?.questionText?.toLowerCase().includes('average') &&
  //     qa[id]?.questionText?.toLowerCase().includes('symptom severity') &&
  //     qa[id]?.questionType === 'slider'
  // );

  // if (avgSeverityKey) {
  //   const avgSeverityValue = get(avgSeverityKey)[0];

  //   if (avgSeverityValue) {
  //     lisQuality.push(
  //       `<li>Average symptom severity rated: <strong>${avgSeverityValue}</strong>/10</li>`
  //     );
  //   }
  // }

  const bilat = get(Q.bilateralQuestionId);

  if (bilat.length) {
    lisQuality.push(`<li>Bilateral symptoms: ${bilat.join(', ')}</li>`);
  }

  const qualityHtml = lisQuality.length
    ? `<p><span style="font-weight: bold;">${SectionTitles.Quality}</span></p><ul>${lisQuality.join('')}</ul>`
    : '';

  // Severity
  const lisSev: string[] = [];
  const primaryAvgRaw = get(Q.intensityQuestionId)[0];
  let avgSource: 'full' | 'abbreviated' | undefined;
  let avgRaw: string | number | undefined = primaryAvgRaw;

  if (primaryAvgRaw !== undefined && primaryAvgRaw !== null && primaryAvgRaw !== '') {
    avgSource = 'full';
  } else {
    const abbreviatedAvgRaw = get(abbreviatedCCSQuestionsIds.averageSeverityQuestionId)[0];

    if (abbreviatedAvgRaw !== undefined && abbreviatedAvgRaw !== null && abbreviatedAvgRaw !== '') {
      avgRaw = abbreviatedAvgRaw;
      avgSource = 'abbreviated';
    } else {
      avgRaw = undefined;
    }
  }

  const worstRaw = get(Q.intensity2QuestionId)[0];
  const enjoy = get(Q.interferedQuestionId)[0];
  const ga = get(Q.generalInterferedQuestionId)[0];

  const avg = typeof avgRaw === 'string' && !isNaN(Number(avgRaw)) ? Number(avgRaw) : avgRaw;
  const worst =
    typeof worstRaw === 'string' && !isNaN(Number(worstRaw)) ? Number(worstRaw) : worstRaw;

  // VAS questions are conditional on "Pain / Discomfort" being selected
  const shouldShowAvg =
    typeof avg === 'number' && (avgSource === 'abbreviated' || hasPainDiscomfort);

  if (shouldShowAvg) {
    lisSev.push(`<li>Pain severity: VAS average <strong>${avg}</strong>/10</li>`);
  }

  if (hasPainDiscomfort) {
    if (typeof worst === 'number') {
      lisSev.push(`<li>Pain severity: VAS high <strong>${worst}</strong>/10</li>`);
    }
  }

  if (enjoy) {
    lisSev.push(`<li>Life enjoyment interference: <strong>${enjoy}</strong>/10</li>`);
  }

  if (ga) {
    lisSev.push(`<li>General activity interference: <strong>${ga}</strong>/10</li>`);
  }

  const severityHtml = lisSev.length
    ? `<p><span style="font-weight: bold;">${SectionTitles.Severity}</span></p><ul>${lisSev.join('')}</ul>`
    : '';

  // Timing
  const lisTim: string[] = [];
  const daysStr = get(Q.percentOfDaysQuestionId)[0];
  const eachStr = get(Q.symptomsArePresentQuestionId)[0];
  const days = daysStr === '0' || daysStr === undefined ? NaN : Number(daysStr || '');
  const each = eachStr === '0' || eachStr === undefined ? NaN : Number(eachStr || '');
  const parts: string[] = [];

  if (isFinite(days)) {
    parts.push(
      `Symptoms present <strong>${timingDescriptor(days, 'days')}</strong> (${days}% of days)`
    );
  }

  if (isFinite(each)) {
    parts.push(
      `<strong>${timingDescriptor(each, 'eachDay')}</strong> each day (${each}% of the day)`
    );
  }

  if (parts.length) {
    lisTim.push(`<li>${parts.join(', ')}</li>`);
  }

  const timingHtml = lisTim.length
    ? `<p><span style="font-weight: bold;">${SectionTitles.Timing}</span></p><ul>${lisTim.join('')}</ul>`
    : '';

  // Provocative / Palliative
  const prov = get(Q.aggravatesQuestionId);
  const provHtml = prov.length
    ? `<span style="font-weight: bold;">${SectionTitles.ProvocativeFactors}</span><ul>${prov
        .map(v => `<li><strong>${v}</strong></li>`)
        .join('')}</ul>`
    : '';
  const pall = get(Q.reliefQuestionId);
  const pallHtml = pall.length
    ? `<span style="font-weight: bold;">${SectionTitles.PalliativeFactors}</span><ul>${pall
        .map(v => `<li><strong>${v}</strong></li>`)
        .join('')}</ul>`
    : '';

  // Additional (collect shared into accumulators)
  const lisAddl: string[] = [];
  const seek = get(Q.seekCareQuestionId)[0];
  const providers = get(Q.providerQuestionId);

  if (seek) {
    lisAddl.push(`<li>Sought care: <strong>${seek}</strong></li>`);
  }

  if (providers.length) {
    // Get customInputData from the question answer if it exists
    const providerQuestionId = useSecondary
      ? Q.providerQuestionId
      : CCSPrimaryQuestionIds.providerQuestionId;
    const providerQA = qa[providerQuestionId];
    const customInputData = providerQA?.customInputData || {};

    // Format each provider with its custom input if available
    const formattedProviders = providers.map(provider => {
      const customInput = customInputData[provider];

      return customInput ? `${provider}: "${customInput}"` : provider;
    });

    lisAddl.push(`<li>Providers seen: <strong>${formattedProviders.join(', ')}</strong></li>`);
  }

  const pregStatus = get(CCSPrimaryQuestionIds.pregnantQuestionId)[0];
  const due = get(CCSPrimaryQuestionIds.pregnantConfirmedDateQuestionId)[0];
  const lmp = get(CCSPrimaryQuestionIds.pregnantSuspectedDateQuestionId)[0];

  if (pregStatus) {
    accumulators.pregnancyStatuses.add(pregStatus);
  }

  if (due) {
    accumulators.pregnancyDueDates.add(due);
  }

  if (lmp) {
    accumulators.pregnancyLmpDates.add(lmp);
  }

  const yf = get(CCSPrimaryQuestionIds.yellowFlagsQuestionId);

  if (yf.length) {
    accumulators.allYellowFlags.push(...yf);
  }

  const addlHtml = lisAddl.length
    ? `<p><span style="font-weight: bold;">${SectionTitles.AdditionalInformation}</span></p><ul>${lisAddl.join('')}</ul>`
    : '';

  // Order: Onset, then Work/Auto injury details, then Provocative/Palliative, then Quality, Severity, Timing, Additional
  return [
    onsetHtml,
    injuryDetailsHtml,
    provHtml,
    pallHtml,
    qualityHtml,
    severityHtml,
    timingHtml,
    addlHtml,
  ]
    .filter(Boolean)
    .join('');
}
